import { simpleSuccessNotification } from 'hooks/notificationHandler'
import {
  useGetSharePointResourceForUploaderQuery,
  useRemoveSharePointUploadByIdMutation,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import { useAnyTrue } from 'hooks/useAnyTrue'
import {
  SharePointUploaderContext,
  SharePointUploaderContextProps,
} from './types'
import { useProjectContext } from 'context/ProjectContext'

const { Provider, useContextFn } =
  createStrictContext<SharePointUploaderContext>('SharePointUploaderContext')

export const useSharePointUploaderContext = useContextFn

const SharePointUploaderProvider: React.FC<SharePointUploaderContextProps> = ({
  children,
  folder,
  parent,
}) => {
  const { projectId } = useProjectContext()
  const {
    data,
    loading: fetching,
    refetch,
  } = useGetSharePointResourceForUploaderQuery({
    ...DefaultQueryHookOptions,
    skip: !folder,
    variables: {
      sharePointResourceInput: {
        projectId,
        byFolder: { folder, parentId: parent.id },
      },
    },
  })
  const [remove, { loading: removing }] = useRemoveSharePointUploadByIdMutation(
    {
      ...DefaultMutationHookOptions,
      onCompleted: ({ removeSharePointResource: { message, removed } }) => {
        if (!removed) return
        refetch()
        simpleSuccessNotification(message)
      },
    }
  )
  const [loading] = useAnyTrue([fetching, removing])

  return (
    <Provider
      value={{
        folder,
        parent,
        remove: (id) => {
          remove({
            variables: { sharePointResourceInput: { projectId, byId: { id } } },
          })
        },
        sharePointResource: data?.sharePointResource ?? null,
        loading,
        refetch,
      }}
    >
      {children}
    </Provider>
  )
}

export default SharePointUploaderProvider
